import assetUtils, { Asset, getITokenAddress } from 'app-lib/assetUtils'
import errorUtils from 'app-lib/errorUtils'
import lendUtils, { IUserYield } from 'app-lib/lendUtils'
import {
  chainConfigs,
  ChainId,
  getBlockExplorerAddressLink,
  isChainSupported,
} from 'bzx-common/src/config/appConfig'
import _ from 'lodash'
import * as mobx from 'mobx'
import { BigNumber } from '@0x/utils'
import RootStore from '../../stores/RootStore'
import AsyncData from 'bzx-common/src/lib/mobx/AsyncData'

export interface ILendItem {
  supplyApr: number
  asset: Asset
  availableLiquidity: string
  insufficientLiquidityToUnlend: boolean
  interestRate: BigNumber
  isLoading: boolean
  iTokenAddress: string
  iTokenExplorerUrl: string
  liquidity: number
  supplyInterestRate: string
  totalBorrow: string
  totalSupply: string
  userYearlyProfit: BigNumber
  userYield?: IUserYield
}

interface ILendStoreProps {
  autoSyncData: boolean
  lendItems: ILendItem[]
  pendingAssetTx: Asset[]
  profitError: Error | null
  userYields: AsyncData<IUserYield[]>
}

export default class LendStore implements ILendStoreProps {
  autoSyncData = false
  pendingAssetTx: Asset[] = []
  rootStore: RootStore
  profitError: Error | null = null

  /** User balances + lend profits */
  userYields = new AsyncData<IUserYield[]>(
    [],
    () => lendUtils.getLendProfits(this.rootStore.fulcrumProvider, this.lendAssets),
    {
      onError(err) {
        errorUtils.decorateError(err, { title: 'Could not get your yield balances' })
        console.error(err)
      },
    }
  )

  get error() {
    return this.userYields.error || this.profitError
  }

  get lendAssets() {
    const { chainId } = this.rootStore.web3Connection
    if (!chainId || !isChainSupported(chainId)) {
      return []
    }
    return chainConfigs[chainId].lending.assets
  }

  /** Takes from the statsStore for general stats */
  get lendItems(): ILendItem[] {
    const { chain, statsStore } = this.rootStore
    if (!chain) {
      return []
    }
    const { chainId } = chain.chainDetails
    const { currentChainStats } = statsStore
    return this.lendAssets.map((asset) => {
      const lendAsset = assetUtils.wrapNativeAsset(chainId, asset)
      const stat = currentChainStats.find((stat) => stat.tokenName === lendAsset)
      const userYield = this.userYields.value.find((yyield) => yyield.asset === lendAsset)
      const interestRate = new BigNumber(stat?.supplyApr || 0)
      const availableLiquidity = stat?.availableLiquidity || 'NaN'
      const iTokenAddress = getITokenAddress(chainId, asset)
      const iTokenExplorerUrl = getBlockExplorerAddressLink(chainId, iTokenAddress)

      return {
        supplyApr: Number(stat?.supplyApr || 0),
        asset,
        availableLiquidity,
        interestRate,
        isLoading: false,
        iTokenAddress,
        iTokenExplorerUrl,
        liquidity: Number(stat?.availableLiquidity || 0),
        supplyInterestRate: stat?.supplyApr || 'NaN',
        totalBorrow: stat?.totalBorrow || 'NaN',
        totalSupply: stat?.totalSupply || 'NaN',
        insufficientLiquidityToUnlend:
          !!stat &&
          !!userYield &&
          userYield.equity.gt(0) &&
          userYield.equity.gt(availableLiquidity),
        userYield,
        userYearlyProfit:
          stat && userYield ? interestRate.div(100).times(userYield.equity) : new BigNumber(0),
      }
    })
  }

  assign(props: Partial<ILendStoreProps>) {
    Object.assign(this, props)
  }

  setPendingAssetTx(asset: Asset) {
    const isPending = this.pendingAssetTx.some((assetTx) => assetTx === asset)
    if (isPending) {
      this.pendingAssetTx = _.without(this.pendingAssetTx, asset)
    } else {
      this.pendingAssetTx = this.pendingAssetTx.concat([asset])
    }
  }

  async lend(asset: Asset, amount: BigNumber) {
    const { transactionStore } = this.rootStore
    const { lendService } = this.rootStore.services
    let tx
    try {
      const txSteps = lendService.lend(asset, amount)
      this.setPendingAssetTx(asset)
      tx = await transactionStore.run('lend', txSteps)
      if (tx.status === 'success') {
        await this.userYields.fetch()
        this.rootStore.statsStore.refreshForCurrentChain()
      }
      return tx
    } catch (err) {
      console.error(err)
    } finally {
      this.setPendingAssetTx(asset)
    }
  }

  async unlend(asset: Asset, amount: BigNumber) {
    const { transactionStore } = this.rootStore
    const { lendService } = this.rootStore.services
    let tx
    try {
      const txSteps = lendService.unlend(asset, amount)
      this.setPendingAssetTx(asset)
      tx = await transactionStore.run('unlend', txSteps)
      if (tx.status === 'success') {
        await this.userYields.fetch()
        this.rootStore.statsStore.refreshForCurrentChain()
      }
      return tx
    } catch (err) {
      console.error(err)
    } finally {
      this.setPendingAssetTx(asset)
    }
  }

  clearError() {
    this.userYields.error = null
    this.profitError = null
  }

  clearData() {
    this.userYields.reset()
    this.clearError()
  }

  async syncData(chainId: ChainId, walletAddress: string) {
    await this.rootStore.statsStore.stats.fetch(chainId)
    if (walletAddress) {
      await this.userYields.fetch()
      const yields = this.userYields.value
      if (yields.length > 0 && yields.some((yyield) => yyield.iTokenPrice.isNaN())) {
        const profitError = new Error('Missing user iToken data from API Graph')
        errorUtils.decorateError(profitError, { title: 'Could not calculate yield profits' })
        this.assign({ profitError })
      }
    }
  }

  autoSync(enabled: boolean) {
    this.autoSyncData = enabled
  }

  init() {
    mobx.autorun(() => {
      const { chainId, walletAddress } = this.rootStore.web3Connection
      if (this.autoSyncData) {
        if (chainId && isChainSupported(chainId)) {
          this.syncData(chainId, walletAddress)
        }
      }
    })
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    mobx.makeAutoObservable(this, { rootStore: false }, { autoBind: true, deep: false })
    this.init()
  }
}
